import { PARENT_TIMELINE_STAGES, STAGE_META } from "@/lib/enrollmentTypes";

/**
 * Horizontal timeline of parent-visible stages, highlighting the current
 * one + marking completed steps. Terminal negative states (declined,
 * withdrawn) are handled separately by the detail page and not rendered
 * here.
 */
export default function ParentTimeline({ currentStage }) {
  const idx = PARENT_TIMELINE_STAGES.indexOf(currentStage);

  // Some valid parent-visible states (for example, future enrollment) are
  // intentionally not a linear step in the application timeline. Avoid
  // rendering every milestone as "upcoming" in those cases.
  if (idx < 0) {
    return (
      <p className="text-sm text-neutral-600">
        Your center is planning the next steps for this enrollment.
      </p>
    );
  }

  return (
    <ol
      data-testid="parent-enrollment-timeline"
      className="flex items-stretch overflow-x-auto pb-1"
    >
      {PARENT_TIMELINE_STAGES.map((stage, i) => {
        const meta = STAGE_META[stage];
        const Icon = meta.icon;
        const done = i < idx;
        const active = i === idx;
        return (
          <li
            key={stage}
            data-testid={`timeline-step-${stage}`}
            data-state={active ? "active" : done ? "done" : "upcoming"}
            className="flex min-w-[120px] flex-1 flex-col items-center"
          >
            <div className="relative w-full">
              {/* Connector */}
              {i > 0 && (
                <div
                  className={`absolute left-0 top-4 h-[2px] w-1/2 ${
                    done || active ? "bg-primary/60" : "bg-neutral-200"
                  }`}
                />
              )}
              {i < PARENT_TIMELINE_STAGES.length - 1 && (
                <div
                  className={`absolute right-0 top-4 h-[2px] w-1/2 ${
                    done ? "bg-primary/60" : "bg-neutral-200"
                  }`}
                />
              )}
              {/* Dot */}
              <div className="relative z-10 mx-auto flex h-8 w-8 items-center justify-center rounded-full border bg-white transition-colors">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full ${
                    active
                      ? "bg-primary text-white"
                      : done
                        ? "bg-primary/15 text-primary-hover"
                        : "bg-neutral-100 text-neutral-400"
                  }`}
                >
                  <Icon size={14} weight={active ? "fill" : "duotone"} />
                </span>
              </div>
            </div>
            <p
              className={`mt-2 text-center text-[11px] font-medium ${
                active
                  ? "text-neutral-900"
                  : done
                    ? "text-primary-hover"
                    : "text-neutral-500"
              }`}
            >
              {meta.short}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
